/**
 * PlanRendererAxonometricMode — renders the product 360° axonometric render frames.
 *
 * Responsibilities:
 *   - Build all frame `<img>` elements simultaneously in the DOM.
 *   - Toggle the active frame via `classList` (zero React setState in drag hot path).
 *   - Plays an intro animation (frame 0 → geographic-north frame) on first load per session.
 *   - Renders `CardinalsOverlay` that fades in while rotating and fades out after 1.5 s.
 *   - Frame count from `projectConfig.app_config.apartments.render_360.config[0].frames`.
 *   - Images are square; displayed at 1.2× scale and clipped to the rectangular container,
 *     cropping the transparent top/bottom margins in the renders.
 */

import { useMemo, useState, useRef, useCallback, useEffect } from 'react'

import { useApp }     from '../../../../app/AppContext.jsx'
import { useCatalog }    from '../../../catalog/context/CatalogContext.jsx'
import { useProduct } from '../../index.js'

import { useI18n }    from '../../../../shared/i18n/useI18n.js'

import { useAxonometricFrames } from '../../hooks/useAxonometricFrames.js'
import { useProductSvg } from '../../hooks/useProductSvg.js'

import { getIsometricAttractiveFrame } from '../../utils/svgAxonometric.js'

import { CardinalsOverlay } from './CardinalsOverlay.jsx'

import { Button }       from '../../../../shared/ui/Button/Button.jsx'
import { ButtonGroup }  from '../../../../shared/ui/Button/ButtonGroup.jsx'
import { 
  RotateLeft,
  RotateRight, }        from '../../../../shared/ui/icons/index.js'

import styles from './PlanRendererAxonometricMode.module.scss'

// ── Constants ─────────────────────────────────────────────────────────────────

// Must match the :global(.frame--active) rule in the SCSS module.
const ACTIVE_FRAME_CLASS = 'frame--active'

/** How long after the last rotation event the CardinalsOverlay stays visible. */
const CARDINALS_HIDE_DELAY_MS = 1500

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * @param {object} _props  — no props; all data comes from context
 */
export function PlanRendererAxonometricMode() {
  const { t }                         = useI18n()
  const { windowData, projectConfig } = useApp()
  const { selection, viewerState, axonometricBridge } = useProduct()

  const { product }         = selection
  const { selectedLevelId } = viewerState

  // ── Derived ───────────────────────────────────────────────────────────────

  const projectDataUrl = windowData?.projectDataUrl ?? null

  const refTier = selectedLevelId ?? product?.tiers?.[0]?.ref_tier ?? null

  // V1: first entry in config array.
  const framesCount = projectConfig?.app_config?.apartments?.render_360?.config?.[0]?.frames ?? 8

  const azimuth = Number(product?.azimuth) || 90

  // ── Attractive frame index — SVG-based analysis ───────────────────────────
  //
  // Determines the best initial frame: the view that shows the apartment's
  // primary outdoor space (balcony/terrace) or largest indoor room at a
  // diagonal (non-en-face) angle. Computed once per product/tier from the 2D
  // plan SVG; the result drives the intro animation target in useAxonometricFrames.
  //
  // Declared BEFORE useAxonometricFrames so that this effect is registered
  // earlier in the React effect queue and fires first, ensuring
  // attractiveFrameRef.current is set before the intro-animation effect reads it.

  const { compartments: compartmentsDictionary } = useCatalog()

  const { svgElement: planSvg } = useProductSvg({ product, refTier, projectDataUrl })

  const attractiveFrameRef = useRef(null)

  useEffect(() => {
    if (!planSvg || !product) {
      attractiveFrameRef.current = null
      return
    }
    attractiveFrameRef.current = getIsometricAttractiveFrame(
      planSvg,
      product.compartments,
      compartmentsDictionary,
      refTier,
      framesCount,
      azimuth,
    )
  }, [planSvg, product, refTier, framesCount, azimuth, compartmentsDictionary])

  // ── Cardinals visibility (fade in on rotate, fade out after idle) ─────────

  const [isRotating, setIsRotating]   = useState(false)
  const cardinalsHideTimerRef         = useRef(null)

  const markRotating = useCallback(() => {
    setIsRotating(true)
    clearTimeout(cardinalsHideTimerRef.current)
    cardinalsHideTimerRef.current = setTimeout(() => setIsRotating(false), CARDINALS_HIDE_DELAY_MS)
  }, [])

  useEffect(() => () => clearTimeout(cardinalsHideTimerRef.current), [])

  // ── Frame player ──────────────────────────────────────────────────────────

  const {
    frameImgRefsRef,
    currentIndexRef,
    isDraggingRef,
    getFrameUrl,
    loadingProgress,
    isLoaded,
    compassAzimuthDeg,
    onImgLoad,
    handleMouseDown:   _handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart:  _handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleRotateLeft:  _handleRotateLeft,
    handleRotateRight: _handleRotateRight,
    handleGoToNorth:   _handleGoToNorth,
  } = useAxonometricFrames({
    projectDataUrl,
    product,
    refTier,
    framesCount,
    azimuth,
    activeFrameClass: ACTIVE_FRAME_CLASS,
    compassElRef: axonometricBridge.compassElRef,
    attractiveFrameRef,
  })

  // Wrap handlers that initiate rotation to trigger Cardinals fade-in.
  const handleMouseDown = useCallback((e) => {
    _handleMouseDown(e)
    markRotating()
  }, [_handleMouseDown, markRotating])

  const handleTouchStart = useCallback((e) => {
    _handleTouchStart(e)
    markRotating()
  }, [_handleTouchStart, markRotating])

  const handleRotateLeft = useCallback(() => {
    _handleRotateLeft()
    markRotating()
  }, [_handleRotateLeft, markRotating])

  const handleRotateRight = useCallback(() => {
    _handleRotateRight()
    markRotating()
  }, [_handleRotateRight, markRotating])

  const handleGoToNorth = useCallback(() => {
    _handleGoToNorth()
    markRotating()
  }, [_handleGoToNorth, markRotating])

  // Register the wrapped handleGoToNorth in the bridge so ViewerToolbar's compass
  // button can trigger it (with CardinalOverlay fade-in) without prop drilling.
  useEffect(() => {
    axonometricBridge.goToNorthRef.current = handleGoToNorth
    return () => { axonometricBridge.goToNorthRef.current = null }
  }, [handleGoToNorth, axonometricBridge.goToNorthRef])

  // ── Frame render order ────────────────────────────────────────────────────

  const frameRenderOrder = useMemo(() => {
    if (!framesCount) return []
    return Array.from({ length: framesCount }, (_, i) => i)
  }, [framesCount])

  // ── Guard ─────────────────────────────────────────────────────────────────

  if (!product || refTier == null) return null

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={[styles.renderer, !isLoaded ? styles.loading : ''].join(' ').trim()}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── Frame images ────────────────────────────────────────────── */}
      <div className={styles.framesHolder}>
        {frameRenderOrder.map((i) => (
          <img
            key={i}
            // eslint-disable-next-line no-return-assign
            ref={(el) => { frameImgRefsRef.current[i] = el }}
            src={getFrameUrl(i)}
            alt=""
            loading="eager"
            decoding="async"
            fetchpriority={i === 0 ? 'high' : 'low'}
            draggable={false}
            onLoad={onImgLoad}
            className={[styles.frame, i === currentIndexRef.current ? ACTIVE_FRAME_CLASS : ''].join(' ').trim()}
          />
        ))}
      </div>

      {/* ── Cardinals overlay — fades in during rotation ─────────────── */}
      <div className={[styles.cardinalsContainer, isRotating ? styles.cardinalsVisible : ''].join(' ').trim()}>
        <CardinalsOverlay azimuthDeg={compassAzimuthDeg} />
      </div>

      {/* ── Rotate controls ─────────────────────────────────────────── */}
      <div className={styles.controls}>
        <ButtonGroup
          size="medium"
        >
          <Button
            type='button'
            kind='icon'
            size='medium'
            onClick={handleRotateLeft}
            title={t('product.axonometric_rotate_left')}
            aria-label={t('product.axonometric_rotate_left')}
          >
            <RotateLeft/>
          </Button>
          <Button
            type='button'
            kind='icon'
            size='medium'
            onClick={handleRotateRight}
            title={t('product.axonometric_rotate_right')}
            aria-label={t('product.axonometric_rotate_right')}
          >
            <RotateRight/>
          </Button>
        </ButtonGroup>
      </div>

      {/* ── Loading overlay ──────────────────────────────────────────── */}
      {!isLoaded && (
        <div className={styles.loadingOverlay} aria-busy="true">
          <div className={styles.loadingBar}>
            <div
              className={styles.loadingBarInner}
              style={{ width: `${loadingProgress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
